import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  Users, Plus, Copy, Check, ExternalLink, Building2, 
  Mail, Calendar, Eye, Trash2, Clock, Link as LinkIcon
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

export const SponsorsTab = () => {
  const [sponsors, setSponsors] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  const revokeInvite = async (code) => {
    if (!window.confirm("Revoke this invite? The code will no longer work.")) return;
    try {
      await axios.delete(`${API}/sponsors/admin/invites/${code}`, { headers: getAuthHeader() });
      fetchData();
    } catch (err) {
      console.error("Error revoking invite:", err);
    }
  };
  const [selectedSponsor, setSelectedSponsor] = useState(null);

  const fetchData = async () => {
    try {
      const [sponsorsRes, invitesRes] = await Promise.all([
        axios.get(`${API}/sponsors/admin/list`, { headers: getAuthHeader() }),
        axios.get(`${API}/sponsors/admin/invites`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      setSponsors(sponsorsRes.data);
      setInvites(invitesRes.data || []);
    } catch (error) {
      console.error("Error fetching sponsors:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const generateInvite = async () => {
    setGeneratingInvite(true);
    try {
      const response = await axios.post(`${API}/sponsors/admin/invite`, {}, { headers: getAuthHeader() });
      setInvites([response.data, ...invites]);
      
      // Copy to clipboard
      const fullUrl = `${window.location.origin}/sponsor/signup?code=${response.data.code}`;
      await navigator.clipboard.writeText(fullUrl);
      setCopiedCode(response.data.code);
      setTimeout(() => setCopiedCode(null), 3000);
    } catch (error) {
      console.error("Error generating invite:", error);
    } finally {
      setGeneratingInvite(false);
    }
  };

  const copyInviteLink = async (code) => {
    const fullUrl = `${window.location.origin}/sponsor/signup?code=${code}`;
    await navigator.clipboard.writeText(fullUrl);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 3000);
  };

  const viewSponsorDetails = async (sponsorId) => {
    try {
      const response = await axios.get(`${API}/sponsors/admin/${sponsorId}`, { headers: getAuthHeader() });
      setSelectedSponsor(response.data);
    } catch (error) {
      console.error("Error fetching sponsor details:", error);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mx-auto"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">
            Sponsor Management
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Invite sponsors and manage their marketing assets
          </p>
        </div>
        <Button 
          onClick={generateInvite}
          disabled={generatingInvite}
          className="bg-white text-black hover:bg-zinc-200 text-xs px-4 py-2 h-auto rounded-none"
          data-testid="generate-invite-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {generatingInvite ? "Generating..." : "Generate Invite"}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-900/50 border border-white/5 p-4">
          <p className="text-2xl font-bold text-white">{sponsors.length}</p>
          <p className="text-xs text-zinc-500 uppercase tracking-widest">Total Sponsors</p>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 p-4">
          <p className="text-2xl font-bold text-green-500">
            {sponsors.filter(s => s.status === 'active').length}
          </p>
          <p className="text-xs text-zinc-500 uppercase tracking-widest">Active</p>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 p-4">
          <p className="text-2xl font-bold text-blue-500">
            {invites.filter(i => !i.used).length}
          </p>
          <p className="text-xs text-zinc-500 uppercase tracking-widest">Pending Invites</p>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 p-4">
          <p className="text-2xl font-bold text-yellow-500">
            {sponsors.reduce((acc, s) => acc + (s.assets?.banners?.length || 0) + (s.assets?.videos?.length || 0), 0)}
          </p>
          <p className="text-xs text-zinc-500 uppercase tracking-widest">Total Assets</p>
        </div>
      </div>

      {/* Recent Invites */}
      {invites.length > 0 && (
        <div className="bg-zinc-900/50 border border-white/10 p-6">
          <h3 className="font-secondary text-lg font-bold uppercase tracking-tight text-white mb-4">
            Invite Links
          </h3>
          <div className="space-y-3">
            {invites.slice(0, 5).map(invite => (
              <div 
                key={invite.code} 
                className={`flex items-center justify-between p-3 border ${
                  invite.used ? 'border-zinc-800 bg-zinc-900/30' : 'border-white/10 bg-black/30'
                }`}
              >
                <div className="flex items-center gap-3">
                  <LinkIcon className={`w-4 h-4 ${invite.used ? 'text-zinc-600' : 'text-zinc-400'}`} />
                  <code className={`text-sm font-mono ${invite.used ? 'text-zinc-600' : 'text-white'}`}>
                    {invite.code}
                  </code>
                  {invite.used ? (
                    <span className="px-2 py-0.5 bg-zinc-800 text-zinc-500 text-[10px] uppercase">Used</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-[10px] uppercase">Active</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500">
                    Expires {new Date(invite.expires_at).toLocaleDateString()}
                  </span>
                  {!invite.used && (
                    <>
                      <button
                        onClick={() => copyInviteLink(invite.code)}
                        className="p-2 text-zinc-500 hover:text-white"
                        title="Copy invite link"
                      >
                        {copiedCode === invite.code ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => revokeInvite(invite.code)}
                        className="p-2 text-zinc-500 hover:text-red-500"
                        title="Revoke invite"
                        data-testid={`revoke-sponsor-invite-${invite.code}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sponsors List */}
      <div>
        <h3 className="font-secondary text-lg font-bold uppercase tracking-tight text-white mb-4">
          Registered Sponsors
        </h3>
        
        {sponsors.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900/50 border border-white/5">
            <Users className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No sponsors registered yet</p>
            <p className="text-xs text-zinc-600 mt-2">
              Generate an invite link to get sponsors signed up
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sponsors.map(sponsor => (
              <div 
                key={sponsor.id} 
                className="bg-zinc-900/50 border border-white/5 p-4 hover:border-white/10 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-secondary text-lg font-bold text-white">
                      {sponsor.company}
                    </h4>
                    <p className="text-sm text-zinc-400">{sponsor.name}</p>
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${
                    sponsor.status === 'active' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-zinc-500/20 text-zinc-400'
                  }`}>
                    {sponsor.status}
                  </span>
                </div>

                <div className="space-y-2 text-xs text-zinc-500 mb-4">
                  <div className="flex items-center gap-2">
                    <Mail className="w-3 h-3" />
                    <span>{sponsor.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-3 h-3" />
                    <span>{sponsor.business_type}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3 h-3" />
                    <span>Joined {new Date(sponsor.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Asset counts */}
                <div className="flex items-center gap-4 text-xs text-zinc-500 mb-4">
                  <span>{sponsor.assets?.logos?.length || 0} logos</span>
                  <span>{sponsor.assets?.banners?.length || 0} banners</span>
                  <span>{sponsor.assets?.videos?.length || 0} videos</span>
                </div>

                <Button
                  onClick={() => viewSponsorDetails(sponsor.id)}
                  className="w-full bg-zinc-800 text-white hover:bg-zinc-700 text-xs px-4 py-2 h-auto rounded-none"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Assets
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sponsor Details Modal */}
      {selectedSponsor && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-[#0a0a0a]">
              <div>
                <h3 className="font-secondary text-lg font-bold uppercase text-white">
                  {selectedSponsor.company}
                </h3>
                <p className="text-sm text-zinc-400">{selectedSponsor.name}</p>
              </div>
              <button 
                onClick={() => setSelectedSponsor(null)} 
                className="text-zinc-500 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="p-4 space-y-6">
              {/* Logos */}
              <div>
                <h4 className="text-sm font-bold uppercase text-zinc-400 mb-3">
                  Logos ({selectedSponsor.assets?.logos?.length || 0})
                </h4>
                {selectedSponsor.assets?.logos?.length > 0 ? (
                  <div className="grid grid-cols-3 gap-3">
                    {selectedSponsor.assets.logos.map(asset => (
                      <div key={asset.id} className="bg-zinc-900 p-2 aspect-square flex items-center justify-center">
                        {asset.file_data && (
                          <img 
                            src={`data:image/${asset.file_extension};base64,${asset.file_data}`}
                            alt={asset.name}
                            className="max-w-full max-h-full object-contain"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-600">No logos uploaded</p>
                )}
              </div>

              {/* Banners */}
              <div>
                <h4 className="text-sm font-bold uppercase text-zinc-400 mb-3">
                  Banners ({selectedSponsor.assets?.banners?.length || 0})
                </h4>
                {selectedSponsor.assets?.banners?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedSponsor.assets.banners.map(asset => (
                      <div key={asset.id} className="bg-zinc-900 p-2">
                        {asset.file_data && (
                          <img 
                            src={`data:image/${asset.file_extension};base64,${asset.file_data}`}
                            alt={asset.name}
                            className="w-full object-contain"
                          />
                        )}
                        <p className="text-xs text-zinc-500 mt-2">{asset.name}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-600">No banners uploaded</p>
                )}
              </div>

              {/* Videos */}
              <div>
                <h4 className="text-sm font-bold uppercase text-zinc-400 mb-3">
                  Pre-Roll Videos ({selectedSponsor.assets?.videos?.length || 0})
                </h4>
                {selectedSponsor.assets?.videos?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {selectedSponsor.assets.videos.map(asset => (
                      <div key={asset.id} className="bg-zinc-900 p-3">
                        <p className="text-sm text-white">{asset.name}</p>
                        <p className="text-xs text-zinc-500">
                          Uploaded {new Date(asset.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-600">No videos uploaded</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default SponsorsTab;
